import { Accordion, Button, Dialog, DialogActions, DialogContent, Grid, Stack } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import Select from '../../../../../components/forms/Select'
import { nbPerPageLabel } from '../../../../../referencial/constants/ReferencialConstants'
import { getParametersNature, getParametersType } from '../../../../../referencial/util/ReferencialUtils'
import { getStatusInformation } from '../../../../../referencial/util/StatusUtils'
import { getLocalizationPicto } from '../../../../../utils/AnalyseUtils'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import useListIndexed from 'utils/customHook/useListIndexed'
import useSandreList from 'utils/customHook/useSandreList'
import { isNil, template, groupBy, keys, includes, compact } from 'lodash'
import useBoolean from 'utils/customHook/useBoolean'
import { StyledFieldSet } from 'components/StyledElements'
import { NewTable } from 'components/datatable/NewTable'
import { QualityCampaignContext } from 'quality/components/campaign/QualityCampaign'
import ConfirmModal from 'components/modal/ConfirmModal'
import { AccordionTitle } from 'components/styled/Accordions'
import { getI18nTitleDataLength } from 'utils/StringUtil'
import { negIdGenerator } from 'campaign/utils/CampaignUtils'
import { whiteColor } from 'utils/constants/ColorTheme'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'

const TABLE_ALL_HEADERS = ['code', 'name', 'type', 'nature', 'status', 'unit', 'fraction', 'support', 'place']
const TABLE_NO_CODE_HEADERS = ['name', 'type', 'nature', 'status', 'unit', 'fraction', 'support', 'place']

const UpdatePopin = ({
    isOpen = false,
    onClose = () => { },
    onValidate = () => { },

    campaignParameter = {},
}) => {
    const {
        parameters,
        units,
        fractions,
        supports,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
    }), shallowEqual)

    const analysisPlaces = useSandreList('ANALYSES.LIEU')

    const indexedParameters = useListIndexed(parameters, 'code')
    const parameter = indexedParameters[campaignParameter.parameterCode]

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [unit, setUnit] = useState()
    const [fraction, setFraction] = useState()
    const [support, setSupport] = useState()
    const [place, setPlace] = useState()

    useEffect(() => {
        if (!isOpen) return

        setUnit(campaignParameter.unitCode)
        setFraction(campaignParameter.fractionCode)
        setSupport(campaignParameter.supportCode)
        setPlace(campaignParameter.placeCode)
    }, [isOpen])

    return (
        <Dialog
            onClose={onClose}
            fullWidth
            maxWidth='md'
            open={isOpen}
            slotProps={{
                paper: {
                    sx: {
                        minHeight: undefined,
                        maxHeight: undefined,
                    },
                },
            }}
        >
            <DefaultDialogTitle
                title={(showSandreCode ? parameter?.labelWithCode : parameter?.name) ?? ''}
                onClose={onClose}
            />
            <DialogContent>
                <StyledFieldSet>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <Select
                                label={i18n.unit}
                                value={unit}
                                options={units}
                                onChange={setUnit}
                                keyLabel={showSandreCode ? 'labelWithCode' : 'name'}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Select
                                label={i18n.fraction}
                                value={fraction}
                                options={fractions}
                                onChange={setFraction}
                                keyLabel={showSandreCode ? 'labelWithCode' : 'name'}
                                keyValue='id'
                            />
                        </Grid>
                        <Grid size={6}>
                            <Select
                                label={i18n.support}
                                value={support}
                                options={supports}
                                onChange={setSupport}
                                keyLabel={showSandreCode ? 'labelWithCode' : 'name'}
                                keyValue='id'
                            />
                        </Grid>
                        <Grid size={6}>
                            <Select
                                label={i18n.place}
                                options={analysisPlaces}
                                onChange={setPlace}
                                value={place}
                                nullLabel='&nbsp;'
                            />
                        </Grid>
                    </Grid>
                </StyledFieldSet>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => onValidate({
                        ...campaignParameter,
                        unitCode: unit,
                        fractionCode: fraction,
                        supportCode: support,
                        placeCode: isNil(place) ? undefined : `${place}`,
                    })}
                    variant='contained'
                    color='primary'
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

UpdatePopin.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    campaignParameter: PropTypes.shape({
        parameterCode: PropTypes.string,
        unitCode: PropTypes.string,
        fractionCode: PropTypes.string,
        supportCode: PropTypes.string,
        placeCode: PropTypes.string,
    }),
}

const TableQualityCampaignParameters = () => {
    const [selectedParamIndex, setSelectedParamIndex] = useState()
    const [selectionToDelete, setSelectionToDelete] = useState()

    const {
        isEditMode,
        setCampaign,
        campaignParameters, setCampaignParameters,
        campaignPlannings, setCampaignPlannings,
        campaignPlanningsExceptions, setCampaignPlanningsExceptions,
    } = useContext(QualityCampaignContext)
    const {
        parameters,
        units,
        fractions,
        supports,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
    }), shallowEqual)

    const indexedUnits = useListIndexed(units, 'code')
    const indexedFractions = useListIndexed(fractions, 'code')
    const indexedSupports = useListIndexed(supports, 'code')
    const indexedParameters = useListIndexed(parameters, 'code')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const types = useMemo(getParametersType, [])
    const natures = useMemo(getParametersNature, [])

    const {
        value: isConfirmOpen,
        setTrue: onOpenConfirm,
        setFalse: onCloseConfirm,
    } = useBoolean(false)
    const {
        value: isSelectionConfirmOpen,
        setTrue: onSelectionOpenConfirm,
        setFalse: onSelectionCloseConfirm,
    } = useBoolean(false)
    const {
        value: isOpen,
        setTrue: onOpen,
        setFalse: onClose,
    } = useBoolean(false)

    const countPlanning = (paramId) => {
        return campaignPlannings.reduce((nb, planning) => {
            if (planning.parametersPlan.includes(paramId)
                || campaignPlanningsExceptions.find(({ planningId, parametersToAddPlan, parametersToDeletePlan }) => (
                    planningId === planning.id && includes([...parametersToAddPlan, ...parametersToDeletePlan], paramId)
                ))
            ) return nb + 1
            return nb
        }, 0)
    }

    const formattedParameters = useMemo(() => {
        const filteredParameters = campaignParameters.filter(p => indexedParameters[p.parameterCode]?.active)

        /* FORMATTAGE POUR REGROUPER LES MÊMES PARAMETRES SUR UNE MEME LIGNE
        const groupedParameters = groupBy(filteredParameters, 'parameterCode')
        return keys(groupedParameters).map(code => {
            const parameter = indexedParameters[code]
            const unitLabels = orderBy(groupedParameters[code], 'unitCode')
                .map(cp => isNil(cp.unitCode) ? i18n.undefined : (indexedUnits[cp.unitCode]?.labelWithCode ?? i18n.unknown))
            const fractionLabels = orderBy(groupedParameters[code], 'fractionCode')
                .map(cp => isNil(cp.fractionCode) ? i18n.undefined : (indexedFractions[cp.fractionCode]?.labelWithCode ?? i18n.unknown))
            const supportLabels = orderBy(groupedParameters[code], 'supportCode')
                .map(cp => isNil(cp.supportCode) ? i18n.undefined : (indexedSupports[cp.supportCode]?.labelWithCode ?? i18n.unknown))

            const placeIcons = orderBy(uniqBy(groupedParameters[code], 'placeCode'), 'placeCode').map(cp => getLocalizationPicto(cp.placeCode))

            return {
                code: parameter.code,
                name: parameter.name,
                type: types[parameter.typeParam],
                nature: natures[parameter.nature],
                status: parameter.status ? getStatusInformation(parameter.status) : i18n.validated,
                unit: uniq(unitLabels).join(', '),
                fraction: uniq(fractionLabels).join(', '),
                support: uniq(supportLabels).join(', '),
                place: (<>{placeIcons}</>),

                data: {
                    parameter: code,
                },
            }
        })
        */

        return groupBy(filteredParameters.map(p => {
            const parameter = indexedParameters[p.parameterCode]
            return {
                code: p.parameterCode,
                name: parameter?.name,
                type: types[parameter?.typeParam],
                nature: natures[parameter?.nature],
                status: parameter?.status ? getStatusInformation(parameter?.status) : i18n.validated,
                unit: showSandreCode ? indexedUnits[p.unitCode]?.labelWithCode : indexedUnits[p.unitCode]?.name,
                fraction: showSandreCode ? indexedFractions[p.fractionCode]?.labelWithCode : indexedFractions[p.fractionCode]?.name,
                support: showSandreCode ? indexedSupports[p.supportCode]?.labelWithCode : indexedSupports[p.supportCode]?.name,
                place: getLocalizationPicto(p.placeCode),

                nbUsedInPlanning: countPlanning(p.id),
                selectionCode: p.selectionCode,
            }
        }), 'selectionCode')
    }, [indexedFractions, indexedParameters, indexedSupports, indexedUnits, natures, campaignParameters, types, showSandreCode])

    const formattedSelectionsWithParameters = useMemo(() => (
        keys(formattedParameters).map(selection => {
            const formattedParams = formattedParameters[selection]?.map(param => (
                {
                    ...param,

                    data: {
                        parameter: param.parameterCode,
                        unit: param.unitCode,
                        fraction: param.fractionCode,
                        support: param.supportCode,
                        place: param.placeCode,
                    },
                }
            ))
            return { name: selection !== 'undefined' ? selection : i18n.outOfSelections, parameters: formattedParams }
        })
    ), [formattedParameters])

    const nbUsedInPlanning = (param) => countPlanning(campaignParameters[param]?.id) ?? 0

    const onUpdateParameter = (index, newParam) => setCampaignParameters(prevCP => prevCP.toSpliced(index, 1, newParam))
    const onDuplicateParameter = (index) => setCampaignParameters(prevCP => [...prevCP, { ...prevCP[index], id: negIdGenerator.next().value }])
    const onDeleteParameter = (index) => {
        const idToDelete = campaignParameters[index]?.id
        setCampaignPlannings(prevCP => prevCP.map(planning => ({ ...planning, parametersPlan: planning.parametersPlan.filter(id => id !== idToDelete) })))
        setCampaignPlanningsExceptions(prevCPE => compact(prevCPE.map(exception => {
            const newException = {
                ...exception,
                parametersToAddPlan: exception.parametersToAddPlan.filter(id => id !== idToDelete),
                parametersToDeletePlan: exception.parametersToDeletePlan.filter(id => id !== idToDelete),
            }
            if (newException.parametersToAddPlan.length === 0 && newException.parametersToDeletePlan.length === 0) return undefined
            return newException
        })))
        setCampaignParameters(prevCP => prevCP.toSpliced(index, 1))
    }
    const onDeleteSelectionParameters = (selection) => {
        setCampaign(prev => ({ ...prev, selections: prev.selections.filter(selec => selec !== selection) }))
        setCampaignParameters(prevCP => prevCP.filter(cP => cP.selectionCode !== selection))
        setCampaignPlannings(prevCP => prevCP.map(planning => ({
            ...planning,
            selection: planning.selection !== selection ? planning.selection : undefined,
            parametersPlan: planning.parametersPlan.filter(idParam =>
                !includes(
                    campaignParameters
                        .filter(({ selectionCode }) => selectionCode === selection)
                        .map(({ id }) => id),
                    idParam,
                ),
            ),
        })))
        setCampaignPlanningsExceptions(prevCPE => compact(prevCPE.map(exception => {
            const newException = {
                ...exception,
                selectionsToAddPlan: exception.selectionsToAddPlan.filter(code => code !== selection),
                selectionsToDeletePlan: exception.selectionsToDeletePlan.filter(code => code !== selection),
                parametersToAddPlan: exception.parametersToAddPlan.filter(idParam => campaignParameters.find(({ id }) => id === idParam)?.selectionCode !== selection),
                parametersToDeletePlan: exception.parametersToDeletePlan.filter(idParam => campaignParameters.find(({ id }) => id === idParam)?.selectionCode !== selection),
            }
            if (newException.parametersToAddPlan.length === 0 && newException.parametersToDeletePlan.length === 0) return undefined
            return newException
        })))
    }

    const getAddOnIndex = (index) => (
        index === 0 ? 0 : formattedSelectionsWithParameters[index - 1].parameters.length + getAddOnIndex(index - 1)
    )

    return (
        <Stack spacing={1}>
            {formattedSelectionsWithParameters.map((selection, i) => {
                const addOnIndex = getAddOnIndex(i)
                const isDisplayed = isEditMode && selection.name === i18n.outOfSelections
                return (
                    <Accordion key={i}>
                        <AccordionTitle
                            title={`${selection.name} (${selection?.parameters?.length || 0} ${getI18nTitleDataLength(i18n.parameter, i18n.parameters, selection?.parameters?.length || 0)})`}
                            actions={[
                                {
                                    icon: 'close',
                                    tooltip: i18n.delete,
                                    onClick: () => {
                                        setSelectionToDelete(selection.name !== i18n.outOfSelections ? selection.name : undefined)
                                        onSelectionOpenConfirm()
                                    },
                                    color: whiteColor,
                                    displayed: isEditMode,
                                },
                            ]}
                        />
                        <NewTable
                            rows={selection.parameters}
                            headers={showSandreCode ? TABLE_ALL_HEADERS : TABLE_NO_CODE_HEADERS}
                            rowsPerPageOptions={nbPerPageLabel}
                            lineActions={
                                [{
                                    icon: 'close',
                                    onClick: (row, index) => {
                                        if (row.nbUsedInPlanning === 0) onDeleteParameter(index + addOnIndex)
                                        else {
                                            setSelectedParamIndex(index + addOnIndex)
                                            onOpenConfirm()
                                        }
                                    },
                                    displayed: isDisplayed,
                                }, {
                                    icon: 'add_to_photos', // duplicate
                                    onClick: (_, index) => onDuplicateParameter(index + addOnIndex),
                                    displayed: isDisplayed,
                                }, {
                                    icon: 'edit',
                                    onClick: (_, index) => {
                                        setSelectedParamIndex(index + addOnIndex)
                                        onOpen()
                                    },
                                    displayed: isEditMode,
                                }]
                            }
                        />
                    </Accordion>
                )
            })}
            <UpdatePopin
                isOpen={isOpen}
                onClose={onClose}
                onValidate={(changes) => {
                    onUpdateParameter(selectedParamIndex, changes)
                    onClose()
                }}

                campaignParameter={campaignParameters[selectedParamIndex]}
            />
            <ConfirmModal
                isOpen={isConfirmOpen || isSelectionConfirmOpen}
                title={i18n.deletingConfirmation}
                subTitle={isSelectionConfirmOpen ? i18n.selectionRemoval
                    : nbUsedInPlanning(selectedParamIndex) > 1 ?
                        template(i18n.parameterUsedInplannings)({ nb: nbUsedInPlanning(selectedParamIndex) })
                        :
                        i18n.parameterUsedInplanning
                }
                onValidate={() => {
                    if (isConfirmOpen) onDeleteParameter(selectedParamIndex)
                    if (isSelectionConfirmOpen) onDeleteSelectionParameters(selectionToDelete)
                    onCloseConfirm()
                    onSelectionCloseConfirm()
                }}
                onClose={() => {
                    onCloseConfirm()
                    onSelectionCloseConfirm()
                }}
            />
        </Stack>
    )
}

TableQualityCampaignParameters.propTypes = {}

export default TableQualityCampaignParameters
